/*
 *
 * Copyright 2022, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */
import './WrcAccrualReports.css';

import React from "react";
import PropTypes from "prop-types";

import {Card, Elevation, HTMLTable} from "@blueprintjs/core";
import {Icon, Spinner, NonIdealState} from "@blueprintjs/core";
import {Tabs, Tab} from "@blueprintjs/core";
import * as IconNames from "@blueprintjs/icons/lib/esm/generated/iconNames";

import {Chart, Pies, Transform} from 'rumble-charts';

import {WrcCardHeader} from "./WrcCardHeader";
import {WrcServer} from "./WrcServer";
import {WrcColors} from "./WrcBranding";

class AccrualReportTab extends React.PureComponent {

    toggleDrilldown(e, data) {
        e.preventDefault();
        if (this.props.getDrilldownStatus() && data.drilldown_value === this.props.getDrilldownValue()) {
            this.props.toggleDrilldownStatus()
        } else {
            this.props.accrualDrilldown(data)
        }
    }

    getClickableRow = (title, short_title) => {
        if (this.props.content.column_groupby) {
            return (<a href="#" onClick={(e) => this.toggleDrilldown(e, {...this.props.content, 'drilldown_value': title})}>{short_title}</a>)
        }
        return short_title
    }

    parseTableData(title, accdata, total, label, denoms, denominator_header) {
        let tablerows = [], keyname = title + '-' + label;
        let denom_total = 0;
        let denom_cell = '';

        for (let i = 0; i < accdata.length; i++) {
            let acc_tabrow = accdata[i];

            let denom = 0;
            if (denoms) {
                if (denoms[acc_tabrow.title]) {
                    denom = denoms[acc_tabrow.title];
                    denom_total += denom*1;
                }
            } else {
                denom = total;
            }
            if (acc_tabrow.y || (denoms && denom)) {
                if (denominator_header) {
                    denom_cell = <td>{denom}</td>
                }
                tablerows.push(
                    <tr key={'acc-tabrow-' + keyname + '-' + i}>
                        <td style={{whiteSpace: 'nowrap'}}><Icon icon={IconNames.SYMBOL_SQUARE}
                                color={acc_tabrow.color}
                                style={{marginRight: '.2em'}}/>
                            {this.getClickableRow(acc_tabrow.title, acc_tabrow.short_title)}
                        </td>
                        <td>{acc_tabrow.y}</td>
                        <td>{denom ? Math.round((acc_tabrow.y / denom) * 100) : '--'}</td>
                        {denom_cell}
                    </tr>
                );
            }
        }

        denom_cell = '';
        if (denominator_header) {
            denom_cell = <td>{denom_total}</td>
        }

        tablerows.push(
            <tr key={'acc-tabrow-totals-' + keyname}>
                <td>Total</td>
                <td>{total}</td>
                <td>{!denom_cell ? ' ' : denom_total ? Math.round((total / denom_total) * 100) : '--'}</td>
                {denom_cell}
            </tr>
        );

        return tablerows;
    }

    constructor(props) {
        super(props);
        this.parseTableData = this.parseTableData.bind(this)
        this.toggleDrilldown = this.toggleDrilldown.bind(this)

        const thedata = props.content,
            tot_all = thedata.total;

        let alldata = [], thecolor = 0;

        if (thedata.responses) {
            for (let row of Object.entries(thedata.responses)) {
                const color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
                alldata.push({title: row[0], y: row[1], color: color, short_title: props.cohort_short_names[row[0]] ? props.cohort_short_names[row[0]] : row[0]});
            }
        }

        const taball = this.parseTableData(props.content.title + '-all', alldata, tot_all, 'All Sites', props.content.denominators, props.content.denominator_header);

        this.state = {
            acc_data_all: [{ data: alldata }],
            cur_data:  [{ data: alldata }],
            table_data_all: taball,
            cur_table: taball,
            tot_all: tot_all
        };
    }

    render() {
        const thetitle =
            <span>
                {this.props.content.title + ' Cases'}
            </span>;

        let renderedChart=undefined;
        if(this.state.tot_all === 0){
            renderedChart=(
                <NonIdealState className='non-ideal-state' icon='pie-chart' title='No Patient Data Accrued' description='Please add patient data for the selected category to see accrual report.'/>
            );
        } else {
            renderedChart= (
                <Chart width={140} height={140} series={this.state.cur_data} className='pie-chart'>
                    <Transform method={['transpose', 'stack']}>
                        <Pies combined={true} />
                    </Transform>
                </Chart>
            );
        }
        const column_header = this.props.content.column_header || ' ';
        const tally_header = this.props.content.tally_header || 'Total';
        const percent_header = this.props.content.percent_header || '%';
        let denominator_header = this.props.content.denominator_header;
        if (denominator_header) {
            denominator_header = (<th>{denominator_header}</th>)
        }
        return (
            <Card className='report-card' interactive={false} style={{minHeight:'570px'}}>
                <WrcCardHeader title={thetitle}/>
                <div className ='summary-chart-container'>
                    {renderedChart}
                </div>

                <div className='summary-table-container'>
                    <HTMLTable condensed={true} bordered={true}
                        key={'accrual-table-' + thetitle}
                        className='summary-table'>
                        <thead>
                        <tr>
                            <th>{column_header}</th>
                            <th>{tally_header}</th>
                            <th>{percent_header}</th>
                            {denominator_header}
                        </tr>
                        </thead>
                        <tbody>
                        {this.state.cur_table}
                        </tbody>
                    </HTMLTable>
                </div>
            </Card>
        );
    }
}

AccrualReportTab.propTypes = {
    showreportshandler: PropTypes.func.isRequired,
    content: PropTypes.object.isRequired,
};

export class WrcAccrualReports extends React.PureComponent {
    parseAccrualData(thecontent) {
        let reporttabs = [];
        for (let i = 0; i < thecontent.length; i++) {
            reporttabs.push(<Tab title={<span>{thecontent[i].title}</span>} id={'report-' + i + '-tab'} key={'report-' + i + '-tab'} panel={
                <AccrualReportTab key={'accrualtab-' + i}
                                    showreportshandler={() => this.showReportsPopup()}
                                    content={thecontent[i]}
                                    cohort_short_names={this.props.cohort_short_names}
                                    accrualDrilldown={this.accrualDrilldown}
                                    getDrilldownValue={this.getDrilldownValue}
                                    toggleDrilldownStatus={this.toggleDrilldownStatus}
                                    getDrilldownStatus={this.getDrilldownStatus}/>
            }/>);
        }

        this.setState({reportTabs: reporttabs});
    }

    constructor(props) {
        super(props);
        this.state = {reportTabs: []};
        this.accrualDrilldown = this.accrualDrilldown.bind(this)
    }

    componentDidMount() {
        this.parseAccrualData(this.props.data)
    }

    capitalize(word) {
        return word[0].toUpperCase() + word.slice(1).toLowerCase();
      }

    toggleDrilldownStatus = () => {
        this.setState({cohort_selected: !this.state.cohort_selected})
    }

    getDrilldownStatus = () => {
        return this.state.cohort_selected
    }

    getDrilldownValue = () => {
        return this.state.drilldown_value
    }

    displayDrilldown() {
        let denom_cell = <td>--</td>;
        let denom_total = '--';
        let thecolor = 0;
        let table_data = this.state.drilldown_data.map((row) => {
            if (this.props.denominator_header) {
                denom_total = this.props.site_denominators[row.site] ? 
                    this.props.site_denominators[row.site][this.state.drilldown_value] ? 
                    this.props.site_denominators[row.site][this.state.drilldown_value] : '--' : '--'
                denom_cell = <td>{denom_total}</td>
            }
            const icon_color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
            return (
                <tr>
                    <td>
                        <Icon icon={IconNames.SYMBOL_SQUARE}
                                        color={icon_color}
                                        style={{marginRight: '.2em'}}/>
                        {row.site}
                    </td>
                    <td>
                        {row.count}
                    </td>
                    <td>{!denom_cell ? ' ' : denom_total !== '--' ? Math.round((row.count / denom_total) * 100) : '--'}</td>
                    {denom_cell}
                </tr>
            )
        })
        const percent_header = this.props.percent_header || '%';
        let denominator_header = this.props.denominator_header;
        if (denominator_header) {
            denominator_header = (<th>{denominator_header}</th>)
        }
        return (
        <span className='summary-table-container'>
            <HTMLTable condensed={true} bordered={true}
                className='summary-table'>
                <thead>
                <tr>
                    <th>{this.capitalize(this.state.grouped_by)}</th>
                    <th>{this.state.drilldown_tally_header}</th>
                    <th>{percent_header}</th>
                    {denominator_header}
                </tr>
                </thead>
                <tbody>
                {table_data}
                </tbody>
            </HTMLTable>
        </span>
        )
    }

    displayChart() {
        let alldata = [], thecolor = 0;

        if (this.state.drilldown_data) {
            for (let i=0; i<this.state.drilldown_data.length; i++) {
                const color = WrcColors.CHART_COLORS_L1[(thecolor++) % WrcColors.CHART_COLORS_L1.length];
                // cohort, site, count
                alldata.push({title: this.state.drilldown_data[i][this.state.grouped_by], y: parseInt(this.state.drilldown_data[i]['count']), color: color, x: i});
            }
        }
        let chart_data = [{data: alldata}]
        let renderedChart=undefined;
        if(this.state.drilldown_data.length === 0){
            renderedChart=(
                <span>
                    <NonIdealState className='non-ideal-state' icon='pie-chart' title='No Patient Data Accrued' description='Please add patient data for the selected category to see accrual report.'/>
                </span>
            );
        } else {
            renderedChart= (
                <span>
                    <Chart width={140} height={140} series={chart_data}  className='pie-chart'>
                        <Transform method={['transpose', 'stack']}>
                            <Pies combined={true} />
                        </Transform>
                    </Chart>
                </span>
            );
        }
        return renderedChart
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.cohort_selected !== this.state.cohort_selected) {
            if (this.state.cohort_selected) {
                document.getElementById('accrual-drilldown').scrollIntoView(false)
            }
        }
    }

    accrualDrilldown = (data) => {
        WrcServer.post('get-site-accrual',
        data,
        (resp) => {
                this.setState({
                    cohort_selected: true,
                    drilldown_cohort: data.title,
                    grouped_by: data.column_groupby,
                    drilldown_value: data.drilldown_value,
                    drilldown_tally_header: data.tally_header,
                    drilldown_data: resp
                })
            }
        )
    }

    render() {
        let card_alignment= this.props.component_alignment === 'center' ? 'center' : 'left';
        let report_full_class= 'report-div ' + card_alignment;
        let drilldown_component = this.state.cohort_selected ?  this.state.drilldown_data.length > 0 ? (                    
        <div>  
            <Card id="accrual-drilldown" className="drilldown-card">
                <WrcCardHeader small={true} title={this.state.drilldown_cohort} />
                <WrcCardHeader small={true} title={this.state.drilldown_value} />
                <div className='rowC'>
                    {this.displayChart()}
                    {this.displayDrilldown()}
                </div>
            </Card>
        </div>) 
        : (<div><Card id="accrual-drilldown" className="drilldown-card"><div><NonIdealState className='non-ideal-state' icon='pie-chart' title='No Accrual Data for Site' description='Please add patient data for the selected cohort to see site report.'/></div></Card></div>) 
        : (<div id="accrual-drilldown"></div>)
        if (!this.state.reportTabs.length) {
            return (
                <div className={report_full_class}>
                    <div>
                        <Spinner/>
                    </div>
                </div>
            );
        } else if (this.state.tot_site === 0) {
            return null;
        } else {
            return (
                    <div>
                        {this.state.reportTabs}
                        { drilldown_component }
                    </div>
            );
        }
    }
}

WrcAccrualReports.propTypes = {
    reports: PropTypes.array.isRequired
};
